import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { HERO_SLIDES, CATEGORIES } from "../lib/constants";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { ArrowRight, Truck, ShieldCheck, Repeat, Sparkles, ChevronLeft, ChevronRight, Tag, Clock, Percent } from "lucide-react";

const CATEGORY_IMAGES = {
  "T-Shirts": "https://images.unsplash.com/photo-1571455786673-9d9d6c194f90?w=500&q=80",
  Shirts: "https://images.unsplash.com/photo-1626557981101-aae6f84aa6ff?w=500&q=80",
  Pants: "https://images.unsplash.com/photo-1552904219-f4b87efe8792?w=500&q=80",
  Tops: "https://images.unsplash.com/photo-1569951498313-24daa07497a8?w=500&q=80",
  Bottoms: "https://images.unsplash.com/photo-1789111161057-90c5ce12dc21?w=500&q=80",
  Dresses: "https://images.unsplash.com/photo-1760287363878-1a09af715b80?w=500&q=80",
  Hoodies: "https://images.unsplash.com/photo-1615397587950-3cbb55f95b77?w=500&q=80",
};

const SALE_WINDOW_MS = 1000 * 60 * 60 * 48; // rolling 48h festive window

function getSaleLeft() {
  const end = Math.ceil(Date.now() / SALE_WINDOW_MS) * SALE_WINDOW_MS;
  let diff = end - Date.now();
  const h = Math.floor(diff / 3.6e6);
  diff -= h * 3.6e6;
  const m = Math.floor(diff / 6e4);
  diff -= m * 6e4;
  const s = Math.floor(diff / 1000);
  return { h, m, s };
}

const KIDS_TILE_IMAGES = [
  { src: "/api/files/bull-creations/products/kids-swing_p-f6f3fc86.jpg", alt: "Boys tropical print shirt set", rot: "rotate-[-3deg]" },
  { src: "/api/files/bull-creations/products/kids-gown_clean-c1dab683.jpg", alt: "Girls floral party gown", rot: "rotate-0" },
  { src: "https://images.unsplash.com/photo-1604482858862-1db908a653e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=500", alt: "Kids everyday tee", rot: "rotate-[3deg]" },
];


function KidsSaleCountdown() {
  const [left, setLeft] = useState(getSaleLeft());
  useEffect(() => {
    const t = setInterval(() => setLeft(getSaleLeft()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  const Box = ({ v, label }) => (
    <div className="flex flex-col items-center">
      <span className="font-mono-price font-extrabold text-xl sm:text-2xl text-[#1D3557] bg-white rounded-lg w-12 sm:w-14 py-1.5 text-center tabular-nums shadow">
        {pad(v)}
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wider text-white/90 mt-1">{label}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2 sm:gap-3 mt-5" data-testid="kids-sale-countdown">
      <Box v={left.h} label="Hrs" />
      <span className="font-display font-extrabold text-xl text-white -mt-4">:</span>
      <Box v={left.m} label="Min" />
      <span className="font-display font-extrabold text-xl text-white -mt-4">:</span>
      <Box v={left.s} label="Sec" />
    </div>
  );
}

function HeroCarousel() {
  const { settings } = useApp();
  const slides = settings?.hero_slides?.length ? settings.hero_slides : HERO_SLIDES;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);
  const go = (d) => setI((p) => (p + d + slides.length) % slides.length);
  return (
    <section className="relative bg-[#1D3557] overflow-hidden" data-testid="hero-carousel">
      <div className="relative h-[360px] sm:h-[440px] lg:h-[520px]">
        {slides.map((s, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <div className="relative max-w-7xl mx-auto h-full px-6 lg:px-10 flex items-end pb-16 sm:pb-20">
              <div className={`${s.align === "right" ? "ml-auto" : ""}`}>
                <Link to={s.link}>
                  <Button className="bg-[#E63946] hover:bg-[#d12836] h-11 px-7 text-base font-semibold rounded-full shadow-lg">
                    {s.cta} <ArrowRight size={18} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => go(-1)} aria-label="prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow z-10">
        <ChevronLeft size={20} />
      </button>
      <button onClick={() => go(1)} aria-label="next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow z-10">
        <ChevronRight size={20} />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} aria-label={`slide ${idx + 1}`}
            className={`h-2 rounded-full transition-all ${idx === i ? "w-7 bg-[#E63946]" : "w-2 bg-white/70"}`} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useApp();
  const categories = settings?.categories?.length ? settings.categories : CATEGORIES;
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then(({ data }) => setFeatured(data.slice(0, 8)));
    api.get("/products?sort=newest").then(({ data }) => setNewArrivals(data.slice(0, 8)));
  }, []);

  return (
    <div>
      <HeroCarousel />

      {/* category circles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-5 sm:gap-8 overflow-x-auto scrollbar-hide pb-2 justify-start sm:justify-center">
          {[{ k: "Men", g: "Men" }, { k: "Women", g: "Women" }, { k: "Kids", g: "Kids" }].map((c) => (
            <Link key={c.k} to={`/shop?gender=${c.g}`} data-testid={`circle-${c.k.toLowerCase()}`} className="group flex flex-col items-center gap-2 shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-[#E63946]/30 p-1 group-hover:border-[#E63946] transition-colors">
                <img src={CATEGORY_IMAGES[c.k === "Men" ? "T-Shirts" : c.k === "Women" ? "Tops" : "Dresses"]} alt={c.k} className="w-full h-full rounded-full object-cover" />
              </div>
              <span className="text-sm font-bold">{c.k}</span>
            </Link>
          ))}
          {categories.slice(0, 5).map((c) => (
            <Link key={c.key} to={`/shop?category=${c.key}`} className="group flex flex-col items-center gap-2 shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-slate-200 p-1 group-hover:border-[#1D3557] transition-colors">
                <img src={CATEGORY_IMAGES[c.key]} alt={c.label} className="w-full h-full rounded-full object-cover" />
              </div>
              <span className="text-sm font-semibold text-slate-600">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* deal strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Percent, t: "Flat 10% OFF", s: "On your first order", c: "from-[#E63946] to-[#f0625f]" },
            { icon: Tag, t: "Buy 2 Get 1", s: "On all T-Shirts", c: "from-[#1D3557] to-[#2a4a73]" },
            { icon: Clock, t: "Festive Sale Live", s: "Up to 70% OFF", c: "from-[#D4AF37] to-[#e8c860]" },
          ].map((d, idx) => (
            <div key={idx} className={`rounded-2xl p-5 bg-gradient-to-br ${d.c} text-white flex items-center gap-4`}>
              <d.icon size={30} className="shrink-0 opacity-90" />
              <div>
                <div className="font-display font-extrabold text-lg">{d.t}</div>
                <div className="text-sm text-white/85">{d.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* trust bar */}
      <section className="bg-white dark:bg-slate-900 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {[
            { icon: Truck, t: "Free Shipping", s: "On orders over ₹1999" },
            { icon: ShieldCheck, t: "Shrink-Proof", s: "Tiruppur guarantee" },
            { icon: Repeat, t: "Easy Returns", s: "7-day returns" },
            { icon: Sparkles, t: "India & GCC", s: "INR & AED pricing" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E63946]/10 flex items-center justify-center text-[#E63946] shrink-0">
                <f.icon size={18} />
              </div>
              <div>
                <div className="font-semibold text-[#0F172A] dark:text-white">{f.t}</div>
                <div className="text-xs text-slate-400">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Shop by Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/shop?category=${c.key}`}
              data-testid={`home-category-${c.key.toLowerCase()}`}
              className="group relative rounded-2xl overflow-hidden aspect-[3/4] bg-slate-100"
            >
              <img
                src={CATEGORY_IMAGES[c.key]}
                alt={c.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <span className="absolute bottom-3 left-0 right-0 text-center text-white font-display font-bold text-sm">
                {c.label}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Kids promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div
          data-testid="kids-promo-tile"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#D4AF37] via-[#f0b84a] to-[#E63946] p-8 sm:p-12 grid md:grid-cols-2 gap-6 items-center"
        >
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/15 blur-xl" />
          <div className="absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-white/10 blur-lg" />
          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 bg-[#1D3557] text-white text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
                🎉 Festive Sale
              </span>
              <span className="inline-flex items-center gap-2 bg-white/90 text-[#E63946] text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
                ✨ New Arrival
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 leading-[1.05] drop-shadow-sm">
              Introducing <span className="text-[#1D3557]">Bull Kids!</span>
            </h2>
            <p className="mt-3 text-white/95 text-base sm:text-lg font-medium max-w-md">
              Soft, playful & super-comfy wear for little ones — party gowns, waistcoat suits, tees, dresses & hoodies.
              <span className="font-extrabold text-[#1D3557]"> Up to 40% OFF</span>, sizes 2–11 years.
            </p>
            <KidsSaleCountdown />
            <Link to="/shop?gender=Kids">
              <Button
                data-testid="kids-promo-cta"
                className="mt-6 bg-white text-[#E63946] hover:bg-slate-100 h-12 px-7 text-base font-bold rounded-full shadow-lg"
              >
                Shop Kids Sale <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
          </div>
          <div className="relative z-10 grid grid-cols-3 gap-3 sm:gap-4 items-start">
            {KIDS_TILE_IMAGES.map((k, idx) => (
              <Link key={k.alt} to="/shop?gender=Kids" className={`block ${idx === 1 ? "" : "mt-8"}`} data-testid={`kids-promo-image-${idx}`}>
                <img
                  src={k.src}
                  alt={k.alt}
                  className={`w-full rounded-2xl aspect-[3/4] object-cover shadow-xl border-4 border-white ${k.rot} hover:rotate-0 hover:scale-[1.03] transition-transform duration-300`}
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tiruppur story */}
      <section className="bg-[#1D3557] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">Our Origin Story</span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 mb-4">
              Born in Tiruppur, India's Knitwear Capital
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Every Bull Creations garment is cut, stitched and finished in Tiruppur — the beating heart of
              India's cotton knitwear industry. We obsess over fabric quality, fit precision and colour
              vibrancy so your daily wear feels premium and lasts longer.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><span className="text-[#D4AF37]">✓</span> 100% combed & bio-washed cotton</li>
              <li className="flex items-center gap-2"><span className="text-[#D4AF37]">✓</span> Shrink-proof & colour-fast finish</li>
              <li className="flex items-center gap-2"><span className="text-[#D4AF37]">✓</span> Ethically manufactured in India</li>
            </ul>
          </div>
          <div className="rounded-3xl overflow-hidden aspect-video">
            <img
              src="https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=900&q=80"
              alt="Tiruppur craftsmanship"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Bestsellers</h2>
          <Link to="/shop" className="text-[#E63946] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
            View All <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* new arrivals */}
      <section className="bg-slate-100 dark:bg-slate-800/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E63946]">Just Dropped</span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold">New Arrivals</h2>
            </div>
            <Link to="/shop?sort=newest" className="text-[#E63946] font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">
              View All <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {newArrivals.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
